import React, { useState } from "react";
import { HeaderContainer, ButtonContainer, Subtitle } from "./Header.styled";
import { Typography, Button, Modal } from "@ann/web-components";
import { useNavigate } from "react-router-dom";
import { useTriggers } from "../../context/TriggersContext";
import { useStore } from "../../../../../hooks";
import { observer } from "mobx-react";
import {
  ProgressModalContainer,
  Spinner,
  SuccessIcon,
  SubText,
  SuccessText,
} from "./Header.styled";
import dayjs from 'dayjs';


const Header = observer(() => {
  const navigate = useNavigate();
  const { createTriggerAction } = useTriggers();
  const authStore = useStore().authStore;
  const [isCreating, setIsCreating] = useState(false);
  const [creatingState, setCreatingState] = useState('idle');
  const currentDateTime = dayjs().format('YYYY-MM-DD HH:mm');
  const defaultTriggerName =  `${authStore.userData.first_name} New Workflow ${currentDateTime}`;

  const getCreatingMessage = () => {
    switch (creatingState) {
      case 'creating-workflow':
        return 'Creating workflow...';
      case 'creating-flow':
        return 'Setting up workflow (this may take up to 20 seconds)...';
      case 'ready':
        return 'Workflow created successfully!';
      default:
        return 'Creating workflow...';
    }
  };

  const handleCreateTrigger = async () => {
    setIsCreating(true);
    setCreatingState('creating-workflow');

    // Set a timeout for the entire process

    const timeout = setTimeout(() => {
      if (creatingState !== 'ready') {
        setCreatingState('idle');
        setIsCreating(false);
        
        const alertData = {
          title: "Workflow Creation Timeout",
          message: "The workflow creation process is taking longer than expected. Please try again or contact support if the issue persists.",
          severity: "error",
        };

        localStorage.setItem("triggerPublishAlert", JSON.stringify(alertData));
        navigate("/v2/workflows");
      }
    }, 45000); // 45 second timeout

    try {
      const triggerData = {
        name:defaultTriggerName,
        description: "",
        type: 'TRIGGER'
      };

      // Indicate we're now creating the workflow
      setCreatingState('creating-flow');
      
      const result = await createTriggerAction(triggerData, authStore.selectedAccount.id);

      // Clear the timeout since we got a response
      clearTimeout(timeout);

      if (result.success) {
        setCreatingState('ready');
        setTimeout(() => {
          const alertData = {
            title: "All set!",
            message: "Your Workflow has been created successfully. You can now build your workflow in the Automation tab.",
            severity: "success",
          };

          localStorage.setItem("triggerPublishAlert", JSON.stringify(alertData));

          // Navigate to the trigger detail page
          navigate(`/v2/workflows/${result.data.uuid}`);
        }, 1000);
      } else {
        // Handle backend errors
        const isFlowCreationError = result.message && (
          result.message.includes('flow') || 
          result.message.includes('workflow') ||
          result.message.includes('AppMixer')
        );
        
        const alertData = {
          title: isFlowCreationError ? "Workflow Creation Failed" : "Workflow not Created",
          message: result.message || "There was a problem creating your workflow. Please try again.",
          severity: "error",
        };

        localStorage.setItem("triggerPublishAlert", JSON.stringify(alertData));
        navigate("/v2/workflows");
      }
    } catch (error) {
      // Clear the timeout
      clearTimeout(timeout);

      const isNetworkError = error.code === 'ERR_NETWORK' || error.message.includes('Network Error');
      const isTimeoutError = error.code === 'ECONNABORTED' || error.message.includes('timeout');
      
      let errorTitle = "Workflow not Created";
      let errorMessage = error.message || "There was a problem creating your workflow. Please try again.";
      
      if (isNetworkError) {
        errorTitle = "Network Error";
        errorMessage = "Unable to connect to the server. Please check your internet connection and try again.";
      } else if (isTimeoutError) {
        errorTitle = "Request Timeout";
        errorMessage = "The request took too long to complete. This may be due to workflow creation delays. Please try again.";
      }

      const alertData = {
        title: errorTitle,
        message: errorMessage,
        severity: "error",
      };

      localStorage.setItem("triggerPublishAlert", JSON.stringify(alertData));
      navigate("/v2/workflows");
    } finally {
      setIsCreating(false);
      setCreatingState('idle');
    }
  };

  return (
    <>
      <HeaderContainer>
        <div>
          <Typography category="title" size="large" weight="bold">
            Workflows
          </Typography>
          <Subtitle>
            <Typography category="body" size="medium" weight="regular">
              Create, manage, and monitor your automated workflows.
            </Typography>
          </Subtitle>
        </div>
        <ButtonContainer>
          <Button
            label={isCreating ? getCreatingMessage() : "Create"}
            size="medium"
            type="gradient"
            onClick={handleCreateTrigger}
            disabled={isCreating}
          />
        </ButtonContainer>
      </HeaderContainer>

      {/* Progress Modal */}
      {isCreating && (
        <Modal
          isOpen={true}
          onClose={() => {}}
          size="medium"
          closeFromOutside={false}
        >
          <Modal.Header icon>
            <Modal.Header.Title>
              Creating Workflow
            </Modal.Header.Title>
            <Modal.Header.Description>
              {getCreatingMessage()}
              <span>{defaultTriggerName}</span>
            </Modal.Header.Description>
          </Modal.Header>
          <Modal.Body>
            <ProgressModalContainer>
              {creatingState === 'creating-workflow' && (
                <>
                  <Spinner className="creating-workflow" />
                  <Typography category="body" size="medium" weight="regular">
                    Creating your workflow...
                  </Typography>
                </>
              )}
              
              {creatingState === 'creating-flow' && (
                <>
                  <Spinner className="creating-flow" />
                  <Typography category="body" size="medium" weight="regular">
                    Setting up AppMixer workflow...
                  </Typography>
                  <Typography category="body" size="small" weight="regular">
                    <SubText>This may take up to 20 seconds</SubText>
                  </Typography>
                </>
              )}
              
              {creatingState === 'ready' && (
                <>
                  <SuccessIcon>✓</SuccessIcon>
                  <Typography category="body" size="medium" weight="regular">
                    <SuccessText>Workflow created successfully!</SuccessText>
                  </Typography>
                </>
              )}
            </ProgressModalContainer>
          </Modal.Body>
        </Modal>
      )}
    </>
  );
});

export default Header;
